import '../mutators/loadMeetingTypesMutator';
import '../mutators/setGetMeetingTypesStateMutator';

import { DatapointStatus, PerformanceDatapoint, logUsage } from 'owa-analytics';
import { trace } from 'owa-trace';

import loadMeetingTypes from '../actions/loadMeetingTypes';
import setFirstTimeUser from '../actions/setFirstTimeUser';
import setGetServicesState from '../actions/setGetMeetingTypesState';
import setMeetingType from '../actions/setMeetingType';
import setNewUserAutoPublishState from '../actions/setNewUserAutoPublishState';
import getEntryPoint from '../selectors/getEntryPoint';
import getMailboxPuid from '../selectors/getMailboxPuid';
import getFirstTimeUser from '../selectors/getFirstTimeUser';
import isDefaultMeetingType, { DEFAULT_MEETING_TYPE } from '../selectors/isDefaultMeetingType';
import getBookingService from '../services/getBookingMeetingType';
import getBookingMeetingTypes from '../services/getBookingMeetingTypes';
import { AutoPublishState, UserOnboardingState } from '../store/schema/BookWithMeOrganizerStore';
import type ServicesRequestStatus from '../store/schema/ServicesRequestStatus';
import checkBookingPageIsPublished from '../utils/checkBookingPagePublished';
import { getUserMailboxGuid } from '../utils/getMailboxGuid';
import setPublishedStatus from '../utils/setPublishedStatus';
import type onboardingEntryPointEnum from '../utils/onboardingEntryPointEnum';
import { isFeatureEnabled } from 'owa-feature-flags';
import type BookingMeetingType from 'owa-service/lib/contract/BookingService';
import type GetBookingServiceResponseMessage from 'owa-service/lib/contract/GetBookingServiceResponseMessage';
import type GetBookingServicesResponseMessage from 'owa-service/lib/contract/GetBookingServicesResponseMessage';
import getMeetingTypeExtension from '../services/getMeetingTypeExtension';
import updateBookingMeetingType from '../services/updateBookingMeetingType';
import { canBypassFeatureCheck } from 'bookwithme-shared';
import isDeletedDefaultMeetingType from '../selectors/isDeletedDefaultMeetingType';
import setUserOnboardingState from '../actions/setUserOnboardingState';

import getOrganizerMeetingTypeId from '../selectors/getOrganizerMeetingTypeId';
import getB2BookingService from 'bookings-shared/lib/c1/services/getBookingService';
import { getUserConfiguration } from 'owa-session-store';
import listBookingServices from 'bookings-shared/lib/c1/services/listBookingServices';
import mapListBookingServicesResponse from 'bookings-shared/lib/c1/mappers/mapListBookingServicesResponse';
import mapB2BookingService from 'bookings-shared/lib/c1/mappers/mapB2BookingService';
import type { BookingService } from 'owa-graph-schema';
import type FeatureSource from 'bookings-shared/lib/c1/constants/source';
import correctFollowUpTime from '../utils/correctFollowUpTime';
import getPageOrigin from '../selectors/getPageOrigin';
import { getGuid } from 'owa-guid';
import { addB2CustomData } from 'bookings-shared/lib/c1/utils/bookingTelemetry';
import getMeetingTypes from '../selectors/getMeetingTypes';

/**
 * TimeOff service id hardcoded guid value.
 */
const TIME_OFF_SERVICE_ID: string = '7759fea6-a78b-4b89-ad68-c6548bc8476a';

interface GetMeetingTypesFromServerOptions {
    publishedStatusAlreadySet?: boolean;
    /**
     * If true, won't set Pending status when performing the request
     */
    silent?: boolean;
}

/**
 * Triggers a service call to get the booking meetingTypes.
 * @param options
 */
// TODO: refactor and refine loop issue and eliminate the optional param publishedStatusAlreadySet
export default async function getMeetingTypesFromServer(
    options?: GetMeetingTypesFromServerOptions
) {
    logUsage('BwmOrganizerPageVisited', {
        pageOrigin: getPageOrigin(),
        entryPoint: getEntryPoint(),
    });
    if (isFeatureEnabled('bookwithme-c1B2Migration')) {
        const perfDatapoint = new PerformanceDatapoint('getBookingMeetingTypes');
        addB2CustomData(perfDatapoint);
        perfDatapoint.addCustomData({
            entryPoint: getEntryPoint(),
            guid: getGuid(),
            mailboxGuid: getUserMailboxGuid(),
            bookingMailboxPuid: getMailboxPuid(),
        });
        try {
            if (!options?.silent) {
                setGetServicesState(1);
            }
            const bookingsSMTPAddress =
                getUserConfiguration().SessionSettings?.UserPrincipalName || '';

            //Calling GetService if meetingId is present
            const organizerMeetingTypeId = getOrganizerMeetingTypeId();
            if (organizerMeetingTypeId) {
                const response = await getB2BookingService(
                    bookingsSMTPAddress,
                    organizerMeetingTypeId,
                    'BookWithMe'
                );
                if (!response.hasError) {
                    const mappedService = mapB2BookingService(
                        response?.service as BookingService,
                        'BookWithMe'
                    );

                    const service = correctFollowUpTime(mappedService);

                    setMeetingType(service);

                    // Save the service into store.MeetingTypes dict.
                    const meetingTypesMap = getMeetingTypes();
                    const serviceId = service.ServiceId;
                    if (!!serviceId) {
                        meetingTypesMap.set(serviceId, service);
                        loadMeetingTypes(meetingTypesMap);
                    }

                    setGetServicesState(2);
                } else {
                    perfDatapoint.endWithError(DatapointStatus.ServerError);
                    setGetServicesState(4);
                }
            } else {
                const response = await listBookingServices(bookingsSMTPAddress, 'BookWithMe');
                // Translate the meetingTypes array into a map for easier use in the code.
                const meetingTypesMap = new Map<string, BookingMeetingType>();
                if (!response.hasError) {
                    const mappedResponse = mapListBookingServicesResponse(response, 'BookWithMe');

                    mappedResponse.services &&
                        mappedResponse.services.map((meetingType: BookingMeetingType) => {
                            const { ServiceId, ExcludeFromSelfService } = meetingType;

                            // Ignore TimeOff service id as that meetingType does not have
                            // any valid purpose for the user.
                            const canIncludeService = ServiceId && !ExcludeFromSelfService;
                            if (canIncludeService) {
                                const updatedMeetingType = correctFollowUpTime(meetingType);
                                meetingTypesMap.set(ServiceId, updatedMeetingType);
                            }
                        });
                    // Save the list of service into store.MeetingTypes dict, then will refresh the ones that have strings trimmed.
                    loadMeetingTypes(meetingTypesMap);
                }
                // After all promises completed, signal request complete, to update UI. In case of empty promises, this line still executes.
                setGetServicesState(2);
            }
        } catch (e) {
            trace.warn(e.message || 'getMeetingTypes failed');
            setGetServicesState(4);
        }
    } else {
        try {
            if (!options?.silent) {
                setGetServicesState(1);
            }
            const result: GetBookingServicesResponseMessage | null = await getBookingMeetingTypes();

            // Translate the meetingTypes array into a map for easier use in the code.
            const meetingTypesMap = new Map<string, BookingMeetingType>();
            if (result) {
                const shouldStartInitialSetup =
                    result.Services?.length === 0 || canBypassFeatureCheck('bwm-reInit');
                if (
                    shouldStartInitialSetup && // if we have not already called preemptInitializePersonalSchedulingPage (which sets isPublished to true then false) then we want to call setPublishedStatus
                    // otherwise we don't want to call setPublishedStatus again since setPublishedStatus calls getMeetingTypesFromServer (causing a loop!)
                    !options?.publishedStatusAlreadySet //This param re-inits BwM flow and we can avoid creating test tenants
                ) {
                    setFirstTimeUser(true);
                    setUserOnboardingState(UserOnboardingState.UserFirstVisitPreFRE);
                    // On First time use of personal bookings, (booking service length is 0, before the first '30 minutes' service is created)
                    // Automatically publish booking pages for new users
                    setPublishedStatus(
                        true /* isPublished: */,
                        true /* isInitializingPublishedStatus */,
                        'bwmInitFlow'
                    ).then(() => {
                        setNewUserAutoPublishState(AutoPublishState.Complete);
                    });

                    checkBookingPageIsPublished();

                    logUsage('InitializingPublishedStatus', {
                        entryPoint: getEntryPoint(),
                        isNewUser: getFirstTimeUser(),
                        mailboxGuid: getUserMailboxGuid(),
                        bookingMailboxPuid: getMailboxPuid(),
                    });

                    logUsage('BwmNewUserOnboarded', {
                        pageOrigin: getPageOrigin(),
                        entryPoint: getEntryPoint(),
                    });
                } else {
                    const defaultMeetingType = result?.Services?.find(
                        service =>
                            service?.ServiceId === DEFAULT_MEETING_TYPE &&
                            !service?.ExcludeFromSelfService
                    );
                    if (defaultMeetingType) {
                        setUserOnboardingState(UserOnboardingState.UserDefaultServiceOnboarded);
                    }
                }

                //TODO: Needs to be removed once DB migration for ExcludeFromSelfService is done gracefully
                const defaultMeetingTypeExtensionResponse = await getMeetingTypeExtension(
                    DEFAULT_MEETING_TYPE
                );

                result.Services &&
                    result.Services.map((meetingType: BookingMeetingType) => {
                        /** Delete default meeting type for new users
                         * If found default meeting type that doesn't have meeting type extension created,
                         * then update the defaultMeetingType.excludeFromSelfServices = true
                         *
                         * Concern: default meeting type might not be actually deleting
                         * Temporary workaround until we create default meeting type with excludeFromService = true from substrate
                         *
                         * TODO: need a better clause to delete default meeting type
                         *  1) when to delete: only run this during publishing state?
                         *  2) what to delete: no meetingTypeExtension created
                         * Remove isDeletingMeetingType after create exludeFromSelfService change is 100%
                         */
                        const { ServiceId, ExcludeFromSelfService } = meetingType;
                        let isDeletingMeetingType = false;
                        const deleteMeetingType = {
                            ...meetingType,
                            ExcludeFromSelfService: true,
                        };
                        // Internal testing purpose:
                        // Bypasses normal flow and updates the ExcludeFromSelfService flag to false.
                        // This forces the existing user to view the default service in UI
                        if (canBypassFeatureCheck('bwm-reInit')) {
                            deleteMeetingType.ExcludeFromSelfService = false;
                            isDefaultMeetingType(ServiceId) &&
                                defaultMeetingTypeExtensionResponse &&
                                updateBookingMeetingType(deleteMeetingType);
                        } else if (
                            isDefaultMeetingType(ServiceId) &&
                            !ExcludeFromSelfService &&
                            !defaultMeetingTypeExtensionResponse
                        ) {
                            isDeletingMeetingType = true;
                            updateBookingMeetingType(deleteMeetingType);
                        }

                        // Ignore TimeOff service id as that meetingType does not have
                        // any valid purpose for the user.
                        // TODO: remove isDeletingMeetingType after create exludeFromSelfService change is 100%
                        const canIncludeService =
                            ServiceId &&
                            ServiceId !== TIME_OFF_SERVICE_ID &&
                            !isDeletingMeetingType &&
                            !isDeletedDefaultMeetingType(ServiceId, !!ExcludeFromSelfService);
                        if (canIncludeService) {
                            meetingTypesMap.set(ServiceId, meetingType);
                        }
                    });
                // Save the list of service into store.MeetingTypes dict, then will refresh the ones that have strings trimmed.
                loadMeetingTypes(meetingTypesMap);
            }
            // getBookingMeetingTypes response returns the string properties upto a max length of 255 chars,
            // OpenBook stores description-Jsonified string that includes 'description', plus
            // themeId, privacyType, version, isAuthRequired, sometimes getting longer than 255.
            // In case the description-jsonified response is trimmed, cannot be properly deserialized,
            // the solution is to refresh the MeetingType using single GetBookingservice API, response has full length strings returned.
            // Check "AdditionalInformation" is not trimmed either, can be used for storing images, etc.
            const getBookingServicePromises: Promise<GetBookingServiceResponseMessage | null>[] =
                [];
            meetingTypesMap.forEach((meetingType: BookingMeetingType) => {
                if (
                    //TODO - https://outlookweb.visualstudio.com/Outlook%20Web/_workitems/edit/206738/?workitem=208962
                    meetingType?.ItemId &&
                    (meetingType?.Description?.length == 255 ||
                        meetingType?.AdditionalInformation?.length == 255)
                ) {
                    // Async call to refresh each service that has strings trimmed, promise is pushed into an array to be used in promise.all
                    getBookingServicePromises.push(getBookingService(meetingType.ItemId));
                }
            });

            // Wait for the response to get the service that needs refresh, then update the service in store.MeetingTypes dict.
            const getBookingServiceMessages = await Promise.all(getBookingServicePromises);
            getBookingServiceMessages.forEach(
                (resultSingleService: GetBookingServiceResponseMessage | null) => {
                    if (resultSingleService?.BookingService?.ServiceId) {
                        resultSingleService = correctFollowUpTime(resultSingleService);
                        resultSingleService?.BookingService &&
                            setMeetingType(resultSingleService?.BookingService);
                    }
                }
            );
            // After all promises completed, signal request complete, to update UI. In case of empty promises, this line still executes.
            setGetServicesState(2);
        } catch (e) {
            trace.warn(e.message || 'getMeetingTypes failed');
            setGetServicesState(4);
        }
    }
}
